import { stamp, type StampStyle } from "@shared/lib/format";

/**
 * Плашки в поле ввода — как в Discord: упоминание, канал, роль и метка времени
 * видны в поле именами («@Макс», «#общий», «1 января 1970 г., 04:00»), а уходят
 * на сервер разметкой (`@max#0001`, `<#…>`, `<@&…>`, `<t:…>`).
 *
 * В поле лежит видимый текст; плашка — кусок этого текста и разметка, которой
 * он станет при отправке. Правка внутри куска плашку снимает: он остаётся
 * обычным текстом.
 */
export interface Pill {
  start: number;
  end: number;
  raw: string;
}

/**
 * Плашки после правки текста: что до правки — на месте, что после — сдвинуто,
 * задетые правкой — сняты. Правка — разница между текстами: общее начало и
 * общий конец, посередине — то, что поменяли.
 */
export function rebase(pills: readonly Pill[], before: string, after: string): Pill[] {
  if (pills.length === 0 || before === after) return [...pills];
  let head = 0;
  const limit = Math.min(before.length, after.length);
  while (head < limit && before[head] === after[head]) head++;
  let tail = 0;
  while (tail < limit - head && before[before.length - 1 - tail] === after[after.length - 1 - tail]) tail++;
  const editEnd = before.length - tail;
  const shift = after.length - before.length;

  const kept: Pill[] = [];
  for (const pill of pills) {
    if (pill.end <= head) kept.push(pill);
    else if (pill.start >= editEnd) kept.push({ ...pill, start: pill.start + shift, end: pill.end + shift });
  }
  return kept;
}

/** Текст к отправке: куски плашек — их разметкой. */
export function serialize(text: string, pills: readonly Pill[]): string {
  let out = "";
  let at = 0;
  for (const pill of [...pills].sort((a, b) => a.start - b.start)) {
    out += text.slice(at, pill.start) + pill.raw;
    at = pill.end;
  }
  return out + text.slice(at);
}

/** Как назвать то, на что указывает разметка; `null` — не знаем, остаётся разметкой. */
export interface PillNames {
  person: (handle: string) => string | null;
  role: (id: string) => string | null;
  channel: (id: string) => string | null;
}

const RAW = /<@&([0-9a-f-]{36})>|<#([0-9a-f-]{36})>|<t:(-?\d{1,13})(?::([tTdDfFR]))?>|(^|[\s(])@(everyone|here|[^\s@#<>]+#\d{4})(?=$|[\s.,!?;:)])/gu;

const named = (prefix: string, name: string | null) => (name ? `${prefix}${name}` : null);

/**
 * Разметка — в видимый текст с плашками: черновик хранится разметкой, а
 * вставленное из буфера и метка из окна времени приходят ею же.
 */
export function hydrate(raw: string, names: PillNames, now = Date.now()): { text: string; pills: Pill[] } {
  let text = "";
  const pills: Pill[] = [];
  let at = 0;
  for (const match of raw.matchAll(RAW)) {
    const [whole, role, channel, seconds, style, lead, person] = match;
    const index = match.index ?? 0;
    let shown: string | null = null;
    let rawPart = whole;
    let before = "";
    if (role) shown = named("@", names.role(role));
    else if (channel) shown = named("#", names.channel(channel));
    else if (seconds !== undefined) shown = stamp(new Date(Number(seconds) * 1000), (style ?? "f") as StampStyle, now);
    else if (person) {
      // Ведущий пробел или скобка — не часть упоминания: остаётся текстом.
      before = lead ?? "";
      rawPart = `@${person}`;
      shown = person === "everyone" || person === "here" ? `@${person}` : named("@", names.person(person));
    }
    if (shown === null) continue;
    text += raw.slice(at, index) + before;
    pills.push({ start: text.length, end: text.length + shown.length, raw: rawPart });
    text += shown;
    at = index + whole.length;
  }
  return { text: text + raw.slice(at), pills };
}

/** Плашка, в которой стоит курсор или которая кончается прямо перед ним. */
export function pillAt(pills: readonly Pill[], caret: number): Pill | undefined {
  return pills.find((pill) => pill.start < caret && caret <= pill.end);
}
